import React, { useMemo } from 'react';

const DEFAULT_PALETTE = [
  '#1E3A5F', // deep navy
  '#B45309', // amber
  '#0F766E', // teal
  '#9F1239', // maroon
  '#475569', // slate
  '#5B6B4D', // sage
  '#6D4AAF', // violet
  '#0369A1', // blue
  '#A16207', // ochre
  '#334155', // charcoal
];

function polarPoint(cx, cy, r, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function layoutColumn(rows, minGap, minY, maxY) {
  const sorted = [...rows].sort((a, b) => a.y - b.y);
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i].y - sorted[i - 1].y < minGap) {
      sorted[i].y = sorted[i - 1].y + minGap;
    }
  }
  const overflowBottom = sorted.length ? sorted[sorted.length - 1].y - maxY : 0;
  if (overflowBottom > 0) sorted.forEach((r) => (r.y -= overflowBottom));
  const overflowTop = sorted.length ? minY - sorted[0].y : 0;
  if (overflowTop > 0) sorted.forEach((r) => (r.y += overflowTop));
  return sorted;
}

export default function LeaderLineDonut({
  data = [],
  width = 640,
  palette = DEFAULT_PALETTE,
  ringThickness = 20,
  gapDegrees = 3,
  minGap = 26,
  minHeight = 240,
  centerLabel = 'Total',
  valueFormatter = (n) => n.toLocaleString(),
  onClick
}) {
  const items = useMemo(
    () => (data || []).filter((d) => (d.quotationCount || 0) > 0),
    [data]
  );
  const total = useMemo(
    () => items.reduce((sum, d) => sum + (d.quotationCount || 0), 0),
    [items]
  );

  const segments = useMemo(() => {
    if (!items.length || !total) return [];
    const n = items.length;
    const usable = 360 - gapDegrees * n;
    let cursor = 0;
    return items.map((item, i) => {
      const sweep = (item.quotationCount / total) * usable;
      const start = cursor;
      const end = cursor + sweep;
      cursor = end + gapDegrees;
      const mid = (start + end) / 2;
      return {
        ...item,
        start,
        end,
        mid,
        side: mid < 180 ? 1 : -1,
        color: palette[i % palette.length],
      };
    });
  }, [items, total, gapDegrees, palette]);

  const maxSideCount = useMemo(() => {
    if (!segments.length) return 0;
    const left = segments.filter((s) => s.side === -1).length;
    const right = segments.filter((s) => s.side === 1).length;
    return Math.max(left, right, 1);
  }, [segments]);

  // Height grows with the number of stacked labels so nothing ever gets
  // squeezed past minGap or pushed outside the drawing area.
  const verticalPadding = 44;
  const height = Math.max(
    minHeight,
    (maxSideCount - 1) * minGap + verticalPadding * 2
  );

  const cx = width / 2;
  const cy = height / 2;
  const sideMargin = 190;
  const outerR = Math.min(height / 2 - 36, width / 2 - sideMargin, 130);
  const stubR = outerR + 14;
  const lineEndInset = 56;

  const labels = useMemo(() => {
    if (!segments.length) return [];
    const raw = segments.map((seg) => {
      const edge = polarPoint(cx, cy, outerR, seg.mid);
      const stub = polarPoint(cx, cy, stubR, seg.mid);
      return { ...seg, edge, stubX: stub.x, y: stub.y };
    });
    const right = layoutColumn(raw.filter((r) => r.side === 1), minGap, 16, height - 16);
    const left = layoutColumn(raw.filter((r) => r.side === -1), minGap, 16, height - 16);
    return [...right, ...left];
  }, [segments, cx, cy, outerR, stubR, height, minGap]);

  const arcPath = (start, end) => {
    const large = end - start > 180 ? 1 : 0;
    const p1 = polarPoint(cx, cy, outerR, start);
    const p2 = polarPoint(cx, cy, outerR, end);
    return `M ${p1.x} ${p1.y} A ${outerR} ${outerR} 0 ${large} 1 ${p2.x} ${p2.y}`;
  };

  if (!items.length) {
    return (
      <div
        style={{ width: '100%', maxWidth: width, height: minHeight }}
        className="flex items-center justify-center text-slate-300 text-xs font-semibold"
      >
        No data available
      </div>
    );
  }

  return (
    <div style={{ width: '100%', maxWidth: width, padding: '0 4px' }}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        style={{ maxWidth: '100%', display: 'block', overflow: 'visible' }}
      >
        {segments.map((seg, i) => (
          <path
            key={seg.name || i}
            d={arcPath(seg.start, seg.end)}
            fill="none"
            stroke={seg.color}
            strokeWidth={ringThickness}
            strokeLinecap="round"
            style={{ cursor: onClick ? 'pointer' : 'default' }}
            onClick={() => onClick && onClick(seg)}
          />
        ))}

        <text
          x={cx}
          y={cy - 6}
          textAnchor="middle"
          style={{ fontSize: 24, fontWeight: 800, fontFamily: 'inherit' }}
          className="fill-slate-800"
        >
          {valueFormatter(total)}
        </text>
        <text
          x={cx}
          y={cy + 14}
          textAnchor="middle"
          style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.08em' }}
          className="fill-slate-400 uppercase"
        >
          {centerLabel}
        </text>

        {labels.map((seg, i) => {
          const lineEndX = seg.side === 1 ? width - lineEndInset : lineEndInset;
          const textX = lineEndX + seg.side * 6;
          return (
            <g
              key={`label-${seg.name || i}`}
              style={{ cursor: onClick ? 'pointer' : 'default' }} // Add cursor pointer style
              onClick={() => onClick && onClick(seg)}               // <-- 3. Call handler on label element click
            >
              <polyline points={`${seg.edge.x},${seg.edge.y} ${seg.stubX},${seg.y} ${lineEndX},${seg.y}`} fill="none" stroke="#CBD5E1" strokeWidth={1} />
              <circle cx={seg.edge.x} cy={seg.edge.y} r={2.5} fill={seg.color} />
              <text x={textX} y={seg.y} textAnchor={seg.side === 1 ? 'start' : 'end'} dominantBaseline="middle" style={{ fontSize: 11, fontWeight: 600, fontFamily: 'inherit' }} className="fill-slate-600">
                {seg.name} <tspan className="fill-slate-400" style={{ fontWeight: 500 }}>({valueFormatter(seg.quotationCount)})</tspan>
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}